import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, X, User, Check } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Scanner UI Component
 * Handles photo capture and AI scorecard analysis
 * Supports multiple players - user selects which score is theirs
 */
const ScannerUI = ({
  numberOfHoles,
  onScanComplete,
  onScoresChange,
  disabled = false
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null);
  const [editedScores, setEditedScores] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setResult(null);
      setPlayers([]);
      setSelectedPlayerIndex(null);
      setEditedScores([]);
    }
  };

  const analyzeScorecard = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('holes', numberOfHoles.toString());

    try {
      const response = await axios.post(`${API}/scan-scorecard`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setResult(response.data);
        const detectedPlayers = response.data.players || [];
        setPlayers(detectedPlayers);
        
        // If only one player, auto-select them
        if (detectedPlayers.length === 1) {
          setSelectedPlayerIndex(0);
          setEditedScores(detectedPlayers[0].scores || []);
        }
        
        toast.success(response.data.message || 'Scorecard analyzed!');
      } else {
        toast.error(response.data.message || 'Could not analyze scorecard');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to analyze scorecard');
    } finally {
      setAnalyzing(false);
    }
  };

  const selectPlayer = (index) => {
    setSelectedPlayerIndex(index);
    setEditedScores([...players[index].scores]);
  };

  const handleScoreEdit = (holeIndex, newScore) => {
    const updated = [...editedScores];
    updated[holeIndex] = parseInt(newScore) || null;
    setEditedScores(updated);
  };

  const clearScan = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setPlayers([]);
    setSelectedPlayerIndex(null);
    setEditedScores([]);
  };

  const getScoreColor = (score, par) => {
    if (!score) return 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-muted)] border-[var(--ps-border)]';
    const diff = score - (par || 4);
    if (diff <= -2) return 'bg-[var(--ps-warning)]/20 text-[var(--ps-warning)] border-[var(--ps-warning)]';
    if (diff === -1) return 'bg-[var(--ps-success)]/20 text-[var(--ps-success)] border-[var(--ps-success)]';
    if (diff === 0) return 'bg-[var(--ps-info)]/20 text-[var(--ps-info)] border-[var(--ps-info)]';
    if (diff === 1) return 'bg-[var(--ps-warning)]/20 text-[var(--ps-warning)] border-[var(--ps-warning)]';
    return 'bg-[var(--ps-error)]/20 text-[var(--ps-error)] border-[var(--ps-error)]';
  };

  // Calculate totals from edited scores
  const front9Scores = editedScores.slice(0, 9);
  const back9Scores = editedScores.slice(9, 18);
  const front9Total = front9Scores.filter(s => s !== null && s !== undefined).reduce((a, b) => a + b, 0) || null;
  const back9Total = back9Scores.filter(s => s !== null && s !== undefined).reduce((a, b) => a + b, 0) || null;
  const totalScore = (front9Total || 0) + (back9Total || 0) || null;

  // Get hole pars from result
  const holePars = result?.hole_pars || [];

  return (
    <div className="space-y-4 pt-4 border-t border-[var(--ps-border)]">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-[var(--ps-text-primary)]">Scan Scorecard</h3>
        <span className="text-sm text-[var(--ps-primary)]">AI-powered detection</span>
      </div>
      
      {!preview ? (
        <div className="space-y-3">
          {/* Take Photo - Direct input for iOS Safari compatibility */}
          <div className="relative">
            <input
              id="scorecard-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleFileChange}
              disabled={disabled}
              data-testid="camera-capture"
            />
            <div className="w-full border-2 border-dashed border-[var(--ps-primary)]/50 rounded-xl p-6 text-center hover:border-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/5 transition-colors">
              <Camera className="w-10 h-10 mx-auto mb-2 text-[var(--ps-primary)]" />
              <p className="text-base font-semibold text-[var(--ps-primary)]">Take Photo</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">Snap your scorecard</p>
            </div>
          </div>
          
          {/* Upload from Gallery */}
          <div className="relative">
            <input
              id="scorecard-gallery-input"
              type="file"
              accept="image/*"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleFileChange}
              disabled={disabled}
              data-testid="gallery-upload"
            />
            <div className="w-full border-2 border-dashed border-[var(--ps-border)] rounded-xl p-4 text-center hover:border-[var(--ps-text-muted)] hover:bg-[var(--ps-bg-elevated)] transition-colors">
              <Upload className="w-6 h-6 mx-auto mb-1 text-[var(--ps-text-muted)]" />
              <p className="text-sm font-medium text-[var(--ps-text-secondary)]">Upload from Gallery</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Preview Image */}
          <div className="relative">
            <img 
              src={preview} 
              alt="Scorecard preview" 
              className="w-full rounded-xl border-2 border-[var(--ps-border)] max-h-64 object-contain bg-[var(--ps-bg-elevated)]" 
            />
            <button
              onClick={clearScan}
              className="absolute top-2 right-2 p-1.5 bg-[var(--ps-error)] text-white rounded-full hover:opacity-90"
              data-testid="clear-scan-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Analyze Button */}
          {!result && (
            <Button
              onClick={analyzeScorecard}
              disabled={analyzing || disabled}
              className="w-full h-12 text-lg font-bold bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5 mr-2" />
                  Analyze Scorecard
                </>
              )}
            </Button>
          )}
          
          {/* Player Selection - Show when multiple players detected */}
          {result && result.success && players.length > 1 && selectedPlayerIndex === null && (
            <div className="space-y-3">
              <p className="text-base font-bold text-[var(--ps-text-primary)] text-center">
                {players.length} Players Found
              </p>
              <p className="text-sm text-[var(--ps-text-secondary)] text-center">
                Tap to select your score
              </p>
              
              <div className="space-y-2">
                {players.map((player, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectPlayer(idx)}
                    className="w-full flex items-center justify-between p-4 bg-[var(--ps-bg-surface)] border-2 border-[var(--ps-border)] rounded-xl hover:border-[var(--ps-primary)] transition-colors"
                    data-testid={`select-player-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[var(--ps-bg-elevated)] flex items-center justify-center">
                        <User className="w-5 h-5 text-[var(--ps-text-muted)]" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-[var(--ps-text-primary)]">{player.name}</p>
                        <p className="text-sm text-[var(--ps-text-secondary)]">
                          {player.scores_found} holes • Total: {player.total || '—'}
                        </p>
                      </div>
                    </div>
                    <span className="text-2xl font-black text-[var(--ps-primary)]">{player.total || '—'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Score Edit UI - Show when player selected OR single player */}
          {result && result.success && selectedPlayerIndex !== null && editedScores.length > 0 && (
            <div className="space-y-3">
              {/* Selected Player Header */}
              {players.length > 1 && (
                <div className="flex items-center justify-between p-3 bg-[var(--ps-primary)]/10 rounded-xl border border-[var(--ps-primary)]/30">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-[var(--ps-primary)]" />
                    <span className="font-bold text-[var(--ps-primary)]">{players[selectedPlayerIndex].name}</span>
                  </div>
                  <button
                    onClick={() => setSelectedPlayerIndex(null)}
                    className="text-sm text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]"
                  >
                    Change
                  </button>
                </div>
              )}
              
              <p className="text-sm text-[var(--ps-success)] bg-[var(--ps-success)]/10 p-2 rounded-lg text-center font-medium">
                Tap any score to edit
              </p>
              
              {/* Score Grid */}
              <div className="space-y-3">
                {/* Front 9 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-[var(--ps-text-secondary)]">Front 9</span>
                    <span className="text-lg font-bold text-[var(--ps-primary)]">{front9Total || '—'}</span>
                  </div>
                  <div className="grid grid-cols-9 gap-1">
                    {front9Scores.map((score, idx) => (
                      <div key={`front-${idx}`} className="text-center">
                        <div className="text-xs text-[var(--ps-text-muted)] mb-1">{idx + 1}</div>
                        <input
                          type="number"
                          min={1}
                          max={15}
                          value={score || ''}
                          onChange={(e) => handleScoreEdit(idx, e.target.value)}
                          className={`w-full h-10 text-center text-lg font-bold rounded-lg border-2 bg-[var(--ps-bg-elevated)] ${getScoreColor(score, holePars[idx])}`}
                          data-testid={`scan-hole-${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Back 9 */}
                {numberOfHoles === 18 && back9Scores.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-[var(--ps-text-secondary)]">Back 9</span>
                      <span className="text-lg font-bold text-[var(--ps-primary)]">{back9Total || '—'}</span>
                    </div>
                    <div className="grid grid-cols-9 gap-1">
                      {back9Scores.map((score, idx) => (
                        <div key={`back-${idx}`} className="text-center">
                          <div className="text-xs text-[var(--ps-text-muted)] mb-1">{idx + 10}</div>
                          <input
                            type="number"
                            min={1}
                            max={15}
                            value={score || ''}
                            onChange={(e) => handleScoreEdit(idx + 9, e.target.value)}
                            className={`w-full h-10 text-center text-lg font-bold rounded-lg border-2 bg-[var(--ps-bg-elevated)] ${getScoreColor(score, holePars[idx + 9])}`}
                            data-testid={`scan-hole-${idx + 10}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Total */}
                <div className="flex items-center justify-between p-3 bg-[var(--ps-primary)]/10 rounded-xl border-2 border-[var(--ps-primary)]/30">
                  <span className="text-lg font-bold text-[var(--ps-text-primary)]">Total Score</span>
                  <span className="text-2xl font-black text-[var(--ps-primary)]">{totalScore || '—'}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={clearScan}
                  className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                  data-testid="retake-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Retake
                </Button>
                <Button
                  onClick={() => onScanComplete && onScanComplete({
                    scores: editedScores,
                    total: totalScore,
                    front9: front9Total,
                    back9: back9Total
                  })}
                  className="flex-1 bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
                  data-testid="use-scores-btn"
                >
                  Use These Scores
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScannerUI;
